// Menu de choix d'un périphérique (feat/preferences-peripheriques).
//
// Remplace <MediaDeviceMenu> de @livekit/components-react, dont il garde le
// DOM et les classes (charte room.css, tests de la scène). Vendor
// (components-react 2.9.24, components-core 0.12.15) a deux défauts :
//   - avec une piste d'aperçu (PreJoin), un clic change bien le
//     périphérique de l'aperçu mais le choix ne REMONTE jamais :
//     setupDeviceSelector publie l'identifiant actif dans un sujet que
//     personne n'écoute. Le PreJoin rejoignait avec le défaut et
//     n'enregistrait rien (Café IA #7, intervenant à deux micros) ;
//   - une entrée est marquée active par identifiant ET, quand l'actif est
//     « default », la première de la liste dès qu'aucun libellé ne
//     commence par l'anglais « Default ». En français l'entrée s'appelle
//     « Par défaut - … » : deux micros en orange quand la première entrée
//     n'est pas le défaut.
// Ici une seule entrée est marquée, et `onSelect` n'est appelé que sur un
// clic : un changement venu du système (périphérique débranché) n'est pas
// un choix à mémoriser.
//
// Accessibilité : une simple liste de boutons (pas de rôle listbox, qui
// promettrait une navigation aux flèches), l'entrée active porte
// `aria-current`, le bouton d'ouverture `aria-expanded` ; Échap ferme et
// rend le focus au bouton.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { RoomEvent } from "livekit-client";
import { computeMenuPosition } from "@livekit/components-core";
import { useMediaDeviceSelect, useRoomContext } from "@livekit/components-react";
import { DEFAULT_DEVICE_ID } from "./device_preferences";

// Index de l'entrée active, -1 si aucune. « default » sans entrée de ce
// nom (Firefox, Safari) : le défaut du système est la première entrée.
function activeDeviceIndex(devices, activeDeviceId) {
  const exact = devices.findIndex((device) => device.deviceId === activeDeviceId);
  if (exact !== -1) return exact;
  return activeDeviceId === DEFAULT_DEVICE_ID && devices.length > 0 ? 0 : -1;
}

export function DeviceMenu({ ariaLabel, devices, activeDeviceId, onSelect, onOpen, disabled = false }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const active = activeDeviceIndex(devices, activeDeviceId);

  // Position tenue à jour même menu fermé (comme vendor) : il s'ouvre déjà
  // à sa place, au-dessus du bouton, retourné ou décalé s'il déborde.
  useLayoutEffect(() => {
    if (disabled || !buttonRef.current || !menuRef.current) return undefined;
    return computeMenuPosition(buttonRef.current, menuRef.current, (x, y) => {
      if (menuRef.current) Object.assign(menuRef.current.style, { left: `${x}px`, top: `${y}px` });
    });
  }, [disabled, devices]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (menuRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("click", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("click", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const toggle = () => {
    if (!open) onOpen?.();
    setOpen(!open);
  };

  const choose = (device) => {
    setOpen(false);
    onSelect(device);
  };

  return (
    <>
      <button
        type="button"
        className="lk-button lk-button-menu"
        data-device-menu=""
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={disabled ? undefined : menuId}
        disabled={disabled}
        onClick={toggle}
        ref={buttonRef}
      />
      {disabled ? null : (
        <div className="lk-device-menu" id={menuId} ref={menuRef} style={{ visibility: open ? "visible" : "hidden" }}>
          <ul className="lk-list lk-media-device-select">
            {devices.map((device, index) => (
              <li key={device.deviceId} data-device-id={device.deviceId} data-lk-active={index === active}>
                <button
                  type="button"
                  className="lk-button"
                  aria-current={index === active ? "true" : undefined}
                  onClick={() => choose(device)}
                >
                  {device.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// Menu de la barre de contrôle, dans la salle : la liste et l'entrée
// active suivent la Room (y compris un changement venu du système).
// `onChoose(kind, device)` n'est appelé qu'après une bascule réussie. Les
// échecs (liste, bascule) passent par l'erreur média de la Room, comme
// chez vendor : bannière de room.jsx.
export function RoomDeviceMenu({ kind, ariaLabel, onChoose }) {
  const room = useRoomContext();
  // Comme vendor : pas de demande d'autorisation avant la première
  // ouverture (sinon une invite au montage de la barre).
  const [requestPermissions, setRequestPermissions] = useState(false);
  const reportError = useCallback((err) => room.emit(RoomEvent.MediaDevicesError, err, kind), [room, kind]);
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
    kind,
    room,
    requestPermissions,
    onError: reportError,
  });

  const select = async (device) => {
    try {
      await setActiveMediaDevice(device.deviceId, { exact: true });
    } catch (err) {
      reportError(err);
      return;
    }
    onChoose(kind, { deviceId: device.deviceId, label: device.label });
  };

  return (
    <DeviceMenu
      ariaLabel={ariaLabel}
      devices={devices}
      activeDeviceId={activeDeviceId}
      onSelect={select}
      onOpen={() => setRequestPermissions(true)}
    />
  );
}
